import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRequirementAuthoringOptions,
  IRequirementDetail,
  RequirementRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import DateOnlyPicker from "./DateOnlyPicker";
import { toDateInputValue, toDateOnly } from "./dateOnlyField";
import ensureOk from "./ensureOk";
import { RecurrenceTypeValue, selectedEnumValue } from "./lookupEnums";

type RequirementValues = {
  name?: string;
  description?: string;
  ruleTypeId?: string;
  measureTypeId?: string;
  amount?: number | string;
  recurrenceTypeId?: string;
  recurrenceIntervalCycles?: number | string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  isAdditive?: boolean;
  isPerEarnPeriod?: boolean;
};

const { Form } = withTypes<RequirementValues>();

type RequirementDialogProps = {
  requirementSetId: string;
  requirement?: IRequirementDetail;
  options?: IRequirementAuthoringOptions;
  open: boolean;
  onClose: () => void;
  onSaved: (requirementId: string) => void;
};

const asNumber = (value?: number | string) =>
  value === "" || value === undefined || value === null ? undefined : Number(value);

const RequirementDialog = ({
  requirementSetId,
  requirement,
  options,
  open,
  onClose,
  onSaved,
}: RequirementDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementService } = useGlobalContext();

  const initialValues: RequirementValues = {
    name: requirement?.name ?? "",
    description: requirement?.description ?? "",
    ruleTypeId: requirement?.ruleTypeId ?? options?.ruleTypes?.[0]?.id ?? "",
    measureTypeId:
      requirement?.measureTypeId ?? options?.measureTypes?.[0]?.id ?? "",
    amount: requirement?.amount ?? "",
    recurrenceTypeId:
      requirement?.recurrenceTypeId ?? options?.recurrenceTypes?.[0]?.id ?? "",
    recurrenceIntervalCycles: requirement?.recurrenceIntervalCycles ?? "",
    effectiveStartDate: toDateInputValue(requirement?.effectiveStartDate),
    effectiveEndDate: toDateInputValue(requirement?.effectiveEndDate),
    isAdditive: !!requirement?.isAdditive,
    isPerEarnPeriod: !!requirement?.isPerEarnPeriod,
  };

  const handleSubmit = async (values: RequirementValues) => {
    try {
      // react-final-form keeps the values of fields that are no longer rendered, so the two
      // conditional groups are dropped explicitly rather than persisted on a requirement whose
      // recurrence no longer has an interval or a window.
      const recurrenceType = selectedEnumValue(
        options?.recurrenceTypes,
        values.recurrenceTypeId
      );
      const isEveryNCycles = recurrenceType === RecurrenceTypeValue.EveryNCycles;
      const isTimeLimited = recurrenceType === RecurrenceTypeValue.TimeLimited;

      const response = await requirementService.saveRequirement(
        RequirementRequest.fromJS({
          id: requirement?.id,
          requirementSetId,
          name: values.name,
          description: values.description || undefined,
          ruleTypeId: values.ruleTypeId,
          measureTypeId: values.measureTypeId,
          amount: asNumber(values.amount) ?? 0,
          recurrenceTypeId: values.recurrenceTypeId,
          recurrenceIntervalCycles: isEveryNCycles
            ? asNumber(values.recurrenceIntervalCycles)
            : undefined,
          effectiveStartDate: isTimeLimited
            ? toDateOnly(values.effectiveStartDate)
            : undefined,
          effectiveEndDate: isTimeLimited
            ? toDateOnly(values.effectiveEndDate)
            : undefined,
          isAdditive: !!values.isAdditive,
          isPerEarnPeriod: !!values.isPerEarnPeriod,
        })
      );

      ensureOk(response);
      onSaved(response.id ?? requirement?.id ?? "");
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const selectField = (
    name: keyof RequirementValues,
    labelId: string,
    items: { id?: string; name?: string }[] | undefined
  ) => (
    <Field name={name}>
      {({ input }) => (
        <Grid item xs={12} sm={6}>
          <TextField
            select
            variant="standard"
            fullWidth
            label={t({ id: labelId })}
            {...input}
          >
            {(items ?? []).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
    </Field>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const recurrenceType = selectedEnumValue(
            options?.recurrenceTypes,
            values.recurrenceTypeId
          );

          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle>
                {t({
                  id: requirement ? "editLicensureRequirement" : "addLicensureRequirement",
                })}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="name" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          required
                          variant="standard"
                          fullWidth
                          label={t({ id: "nameLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {selectField("ruleTypeId", "ruleType", options?.ruleTypes)}
                  {selectField(
                    "measureTypeId",
                    "measureType",
                    options?.measureTypes
                  )}

                  <Field name="amount" required>
                    {({ input, meta }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          variant="standard"
                          fullWidth
                          label={t({ id: "amountLabel" })}
                          InputProps={{
                            type: "number",
                            inputProps: { step: "0.25" },
                          }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {selectField(
                    "recurrenceTypeId",
                    "recurrenceType",
                    options?.recurrenceTypes
                  )}

                  {/* Only an every-N-cycles requirement has an interval. */}
                  {recurrenceType === RecurrenceTypeValue.EveryNCycles && (
                    <Field name="recurrenceIntervalCycles">
                      {({ input }) => (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            label={t({ id: "recurrenceIntervalCycles" })}
                            InputProps={{ type: "number" }}
                            {...input}
                          />
                        </Grid>
                      )}
                    </Field>
                  )}

                  {/* Only a time-limited requirement has its own window. */}
                  {recurrenceType === RecurrenceTypeValue.TimeLimited && (
                    <>
                      <Field name="effectiveStartDate">
                        {({ input }) => (
                          <Grid item xs={12} sm={6}>
                            <DateOnlyPicker
                              label={t({ id: "effectiveStartDate" })}
                              value={input.value}
                              onChange={input.onChange}
                            />
                          </Grid>
                        )}
                      </Field>
                      <Field name="effectiveEndDate">
                        {({ input }) => (
                          <Grid item xs={12} sm={6}>
                            <DateOnlyPicker
                              label={t({ id: "effectiveEndDate" })}
                              value={input.value}
                              onChange={input.onChange}
                            />
                          </Grid>
                        )}
                      </Field>
                    </>
                  )}

                  <Field name="description">
                    {({ input }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          multiline
                          rows={3}
                          label={t({ id: "descriptionLabel" })}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="isAdditive" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                          }
                          label={t({ id: "additiveLabel" })}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="isPerEarnPeriod" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                          }
                          label={t({ id: "isPerEarnPeriod" })}
                        />
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button disabled={submitting} onClick={onClose}>
                  {t({ id: "cancelButton" })}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: requirement ? "saveButton" : "addRequirement" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

export default RequirementDialog;
